import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { ColorPicker } from "./ColorPicker";
import { BairrosPicker } from "./BairrosPicker";
import { emptyRota, isHex } from "./rotasHelpers";

// Dialog Nova/Editar Rota (Iter 98). `rota=null` → criação.
export function RotaDialog({ open, onClose, rota, bairros, rotas, onSaved }) {
  const [form, setForm] = useState(emptyRota());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(rota ? { nome: rota.nome || "", cor: rota.cor || "#3B82F6", ativa: rota.ativa !== false, bairro_ids: rota.bairro_ids || [] } : emptyRota());
  }, [open, rota]);

  const conflicts = useMemo(() => {
    const m = {};
    rotas.filter((r) => r.id !== rota?.id).forEach((r) => (r.bairro_ids || []).forEach((b) => { (m[b] = m[b] || []).push(r.nome); }));
    return m;
  }, [rotas, rota]);

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Informe o nome da rota");
    if (!isHex(form.cor)) return toast.error("Cor inválida — use o formato #RRGGBB");
    setSaving(true);
    try {
      if (rota?.id) await api.put(`/rotas/${rota.id}`, form);
      else await api.post("/rotas", form);
      toast.success(rota?.id ? "Rota atualizada" : `Rota "${form.nome.trim()}" criada`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar rota");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rota-dialog">
        <DialogHeader><DialogTitle>{rota?.id ? "Editar Rota" : "Nova Rota"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nome da rota</Label>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex.: Rota Centro, Zona Norte…" autoFocus data-testid="rota-nome-input" />
          </div>
          <div>
            <Label>Cor</Label>
            <ColorPicker value={form.cor} onChange={(cor) => setForm({ ...form, cor })} />
          </div>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <div className="text-sm font-medium">Rota ativa</div>
              <div className="text-xs text-muted-foreground">Inativa: não recebe entregas automaticamente pelo bairro</div>
            </div>
            <Switch checked={form.ativa} onCheckedChange={(v) => setForm({ ...form, ativa: v })} data-testid="rota-ativa-switch" />
          </div>
          <BairrosPicker bairros={bairros} value={form.bairro_ids} onChange={(bairro_ids) => setForm({ ...form, bairro_ids })} conflicts={conflicts} />
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} data-testid="rota-cancel-btn">Cancelar</Button>
            <Button type="button" className="bg-orange-600 hover:bg-orange-700" onClick={save} disabled={saving} data-testid="rota-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salvar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
